// The picture of two shapes side by side, with letters at their corners,
// small marks across their sides, arcs and right-angle squares in their
// corners, and labels. Plain React SVG, like practice-shape.tsx.

import {
  NOTE_SIZE,
  PAIR_LABEL_SIZE,
  arcPath,
  describeShapePair,
  pathThrough,
  shapePairPicture,
  type PairLabel,
  type ShapePairSpec,
} from './practice-shape-pair-data';

const INK = '#2d4a3e';
const SHAPE_FILL = '#eef3ee';

const fixed = (value: number) => value.toFixed(2);

export function PairText({
  label,
  where,
  size = PAIR_LABEL_SIZE,
  bold = true,
}: {
  label: PairLabel;
  where: string;
  size?: number;
  bold?: boolean;
}) {
  return (
    <text
      data-label={where}
      x={fixed(label.x)}
      y={fixed(label.y)}
      dy="0.35em"
      textAnchor="middle"
      fill={INK}
      fontSize={size}
      fontWeight={bold ? '700' : '400'}
      stroke="#ffffff"
      strokeWidth="3"
      paintOrder="stroke"
    >
      {label.text}
    </text>
  );
}

export function ShapePairView({ pair }: { pair: ShapePairSpec }) {
  const picture = shapePairPicture(pair);
  const { box } = picture;

  return (
    <figure className="m-0 max-w-lg">
      <svg
        role="img"
        aria-label={describeShapePair(pair)}
        viewBox={`${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(box.width * 1.4))}px` }}
      >
        {picture.shapes.map((shape, index) => (
          <g key={index} data-shape={index === 0 ? 'left' : 'right'}>
            <path
              data-outline="true"
              d={pathThrough(shape.outline, true)}
              fill={SHAPE_FILL}
              stroke={INK}
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            {shape.arcs.map((arc, arcIndex) => (
              <path
                key={arcIndex}
                data-mark="arc"
                d={arcPath(arc)}
                fill="none"
                stroke={INK}
                strokeWidth="1.5"
              />
            ))}
            {shape.squares.map((square, squareIndex) => (
              <path
                key={squareIndex}
                data-mark="square"
                d={pathThrough(square, false)}
                fill="none"
                stroke={INK}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            ))}
            {shape.ticks.map((tick, tickIndex) => (
              <line
                key={tickIndex}
                data-mark="tick"
                x1={fixed(tick.from.x)}
                y1={fixed(tick.from.y)}
                x2={fixed(tick.to.x)}
                y2={fixed(tick.to.y)}
                stroke={INK}
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            ))}
            {shape.letters.map((label, labelIndex) => (
              <PairText key={labelIndex} label={label} where="corner" />
            ))}
            {shape.sideLabels.map((label, labelIndex) => (
              <PairText key={labelIndex} label={label} where="side" />
            ))}
            {shape.angleLabels.map((label, labelIndex) => (
              <PairText key={labelIndex} label={label} where="angle" />
            ))}
            {shape.name ? <PairText label={shape.name} where="name" /> : null}
          </g>
        ))}
        {picture.note ? (
          <PairText
            label={picture.note}
            where="note"
            size={NOTE_SIZE}
            bold={false}
          />
        ) : null}
      </svg>
    </figure>
  );
}
